/**
 * CoralDetailModal — the editable coral pop-up shared by every Interactive
 * Guide view (Cards, Compact list, Mind map) and the Collection Manager.
 * Clicking a coral opens this; the operator can edit color tags, growth
 * form, names, descriptions, and the TSA link inline, and the changes are
 * saved to the Pi as a catalog-edit overlay. "Revert to catalog" clears the
 * overlay for that coral.
 */
import { useEffect, useState } from 'react';

import { Button } from '../components/Button';
import { Input } from '../components/ui/Input';

import {
  COLOR_HEX,
  cleanText,
  colonyPhotoUrl,
  fragPhotoUrl,
} from './catalog';
import {
  COLOR_TAGS,
  GROWTH_FORMS,
  type ColorTag,
  type Coral,
  type CoralEdit,
  type GrowthForm,
} from './types';

export interface CoralDetailModalProps {
  readonly open: boolean;
  readonly coral: Coral | null;
  readonly hasEdit: boolean;
  readonly onClose: () => void;
  readonly onSave: (coralId: string, edit: CoralEdit) => void;
  readonly onRevert: (coralId: string) => void;
}

interface FormState {
  name: string;
  species: string;
  colors: ColorTag[];
  growthForm: GrowthForm;
  bodyColor: string;
  polypColor: string;
  tsaUrl: string;
}

function toForm(coral: Coral): FormState {
  return {
    name: coral.name,
    species: coral.species,
    colors: [...coral.colors],
    growthForm: coral.growthForm,
    bodyColor: cleanText(coral.bodyColor),
    polypColor: cleanText(coral.polypColor),
    tsaUrl: coral.tsaUrl,
  };
}

export function CoralDetailModal({
  open,
  coral,
  hasEdit,
  onClose,
  onSave,
  onRevert,
}: CoralDetailModalProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [fragBroken, setFragBroken] = useState(false);
  const [colonyBroken, setColonyBroken] = useState(false);

  useEffect(() => {
    if (open && coral) {
      setForm(toForm(coral));
      setFragBroken(false);
      setColonyBroken(false);
    }
  }, [open, coral]);

  if (!open || !coral || !form) return null;

  const toggleColor = (c: ColorTag) => {
    setForm((prev) => {
      if (!prev) return prev;
      const has = prev.colors.includes(c);
      const colors = has
        ? prev.colors.filter((x) => x !== c)
        : [...prev.colors, c];
      return { ...prev, colors: colors.length > 0 ? colors : prev.colors };
    });
  };

  const handleSave = () => {
    const edit: CoralEdit = {
      name: form.name.trim() || coral.name,
      species: form.species.trim(),
      colors: form.colors,
      growthForm: form.growthForm,
      bodyColor: form.bodyColor.trim(),
      polypColor: form.polypColor.trim(),
      tsaUrl: form.tsaUrl.trim(),
    };
    onSave(coral.id, edit);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Coral details"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(2,6,13,0.78)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-bg-elevated p-5 shadow-glass ring-1 ring-surface-stroke">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-text-1">Edit coral</h2>
            <p className="text-xs text-text-3">
              Changes save to your Pi and apply everywhere.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-text-3 transition-colors hover:bg-surface-glass hover:text-text-1"
          >
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              <line x1="2" y1="2" x2="12" y2="12" />
              <line x1="12" y1="2" x2="2" y2="12" />
            </svg>
          </button>
        </div>

        {/* Photos */}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <figure className="overflow-hidden rounded-lg bg-bg-mid ring-1 ring-surface-stroke">
            <div className="flex aspect-[4/3] items-center justify-center">
              {fragBroken ? (
                <span className="text-xs text-text-3">No frag photo</span>
              ) : (
                <img
                  src={fragPhotoUrl(coral)}
                  alt={`${coral.name} frag`}
                  className="h-full w-full object-cover"
                  onError={() => setFragBroken(true)}
                />
              )}
            </div>
            <figcaption className="px-2 py-1 text-center text-[10px] uppercase tracking-wide text-text-3">
              Frag
            </figcaption>
          </figure>
          <figure className="overflow-hidden rounded-lg bg-bg-mid ring-1 ring-surface-stroke">
            <div className="flex aspect-[4/3] items-center justify-center">
              {colonyBroken ? (
                <span className="text-xs text-text-3">No colony photo</span>
              ) : (
                <img
                  src={colonyPhotoUrl(coral)}
                  alt={`${coral.name} colony`}
                  className="h-full w-full object-cover"
                  onError={() => setColonyBroken(true)}
                />
              )}
            </div>
            <figcaption className="px-2 py-1 text-center text-[10px] uppercase tracking-wide text-text-3">
              Colony
            </figcaption>
          </figure>
        </div>

        {/* Name + species */}
        <div className="mb-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-2">Name</span>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => (p ? { ...p, name: e.target.value } : p))}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-2">Species</span>
            <Input
              value={form.species}
              onChange={(e) => setForm((p) => (p ? { ...p, species: e.target.value } : p))}
            />
          </label>
        </div>

        {/* Colors */}
        <div className="mb-3">
          <span className="mb-1.5 block text-xs font-medium text-text-2">Colors</span>
          <div className="flex flex-wrap gap-1.5">
            {COLOR_TAGS.map((c) => {
              const active = form.colors.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleColor(c)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    active
                      ? 'border-accent/60 bg-accent-soft text-text-1'
                      : 'border-surface-stroke bg-surface-glass text-text-3 hover:text-text-2'
                  }`}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: COLOR_HEX[c] }}
                  />
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Growth form */}
        <div className="mb-3">
          <span className="mb-1.5 block text-xs font-medium text-text-2">Growth form</span>
          <div className="flex flex-wrap gap-1.5">
            {GROWTH_FORMS.map((g) => {
              const active = form.growthForm === g;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setForm((p) => (p ? { ...p, growthForm: g } : p))}
                  aria-pressed={active}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    active
                      ? 'border-accent/60 bg-accent-soft text-text-1'
                      : 'border-surface-stroke bg-surface-glass text-text-3 hover:text-text-2'
                  }`}
                >
                  {g}
                </button>
              );
            })}
          </div>
        </div>

        {/* Descriptions */}
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-text-2">Body color</span>
          <textarea
            value={form.bodyColor}
            onChange={(e) => setForm((p) => (p ? { ...p, bodyColor: e.target.value } : p))}
            rows={2}
            className="block w-full rounded-lg bg-surface-glass px-3 py-2 text-sm text-text-1 ring-1 ring-surface-stroke focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-text-2">Polyp color</span>
          <textarea
            value={form.polypColor}
            onChange={(e) => setForm((p) => (p ? { ...p, polypColor: e.target.value } : p))}
            rows={2}
            className="block w-full rounded-lg bg-surface-glass px-3 py-2 text-sm text-text-1 ring-1 ring-surface-stroke focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="mb-4 block">
          <span className="mb-1 block text-xs font-medium text-text-2">TSA link</span>
          <Input
            value={form.tsaUrl}
            onChange={(e) => setForm((p) => (p ? { ...p, tsaUrl: e.target.value } : p))}
            placeholder="https://topshelfaquatics.com/…"
          />
        </label>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            {form.tsaUrl.trim() !== '' && (
              <a
                href={form.tsaUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-lg border border-surface-stroke px-3 py-2 text-sm text-text-2 hover:text-text-1"
              >
                Open on TSA
              </a>
            )}
            {hasEdit && (
              <Button variant="ghost" onClick={() => { onRevert(coral.id); onClose(); }}>
                Revert to catalog
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
